import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../store/store';
import { Company as CompanyType } from '../../types';
import ConfirmModal from '../ConfirmationModal/ConfirmationModal';
import ControlPanel from '../ControlPanel/ControlPanel';
import CustomerListHead from '../Customer/CustomerList/CustomerListHead/CustomerListHead';
import style from './Company.module.css';
import {
  getPlanClass,
  handleConfirmDelete,
  loadCompanies,
  resendCompanyPasswordAndQr,
} from './companyHelpers';
import MoreVert from './icons/MoreVert';
import ModalWindowCompany from './ModalWindowCompany/ModalWindowCompany';
import SuccessModal from './SuccessModal/SuccessModal';
import { showSnackbar } from '../../store/reducers/snackbarSlice';
import CompanyForm from './СompanyForm/CompanyForm';
import {
  fetchAllCompanies,
  exportCompaniesToExcel,
} from '../../utils/exportCompaniesToExcel';

const AVATAR_COLORS = [
  '#0a8081',
  '#2f6fed',
  '#7c3aed',
  '#db2777',
  '#ea580c',
  '#16a34a',
  '#0891b2',
  '#9333ea',
];
const avatarColor = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const initialOf = (s: string) => (s.trim()[0] || '?').toUpperCase();

const CopyIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const CalendarIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const PhoneIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const SkeletonRows: React.FC = () => (
  <>
    {Array.from({ length: 7 }).map((_, i) => (
      <div className={style.skeletonRow} key={`sk-${i}`}>
        <span className={`${style.skel} ${style.skelAvatar}`} />
        <span className={`${style.skel} ${style.skelLine}`} />
        <span className={`${style.skel} ${style.skelLine}`} />
        <span className={`${style.skel} ${style.skelPill}`} />
        <span className={`${style.skel} ${style.skelLine}`} />
        <span className={`${style.skel} ${style.skelLine}`} />
      </div>
    ))}
  </>
);

const Company: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { companies, searchResults, loading, orderDirection, hasMore } =
    useSelector((state: RootState) => state.companies);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isRotated, setIsRotated] = useState<boolean>(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showCompanyForm, setShowCompanyForm] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const companyRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const pageIndexRef = useRef(0);
  const orderDirectionRef = useRef(orderDirection);
  const orderByRef = useRef('name');
  const isInitialMount = useRef(true);
  const [buttonRef, setButtonRef] = useState<{
    current: HTMLButtonElement | null;
  } | null>(null);
  // §FB31 «Локації»: компанія, для якої відкрито модалку адрес впровадження.
  const [isExporting, setIsExporting] = useState<boolean>(false);
  // Поточний рядок пошуку (для Excel-експорту — щоб вивантажувати саме те, що
  // показано на екрані, як і на сторінці Клієнтів).
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      loadCompanies(dispatch, pageIndexRef, orderDirectionRef, orderByRef);
    }
  }, [dispatch]);

  // Автовідкриття картки компанії після переходу з картки дзвінка (Історія перекладів):
  // читаємо GUID власника з navigation-state, підвантажуємо повну картку й відкриваємо форму.
  useEffect(() => {
    const ownerId = (location.state as { openCompanyOwnerId?: string } | null)
      ?.openCompanyOwnerId;
    if (!ownerId) return;
    // §FB37: картка компанії тепер СТОРІНКА — старі переходи зі state редіректимо на неї.
    navigate(`/company/${ownerId}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
      pageIndexRef.current = 0;
      loadCompanies(dispatch, pageIndexRef, orderDirectionRef, orderByRef);
    }
  };

  // Вивантаження у стилізований .xlsx саме того, що показано (за поточним
  // пошуком і сортуванням) з усіма полями картки. Аналогічно до сторінки Клієнтів.
  const handleExportExcel = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const list = await fetchAllCompanies(
        orderDirectionRef.current,
        searchQuery,
        orderByRef.current,
      );
      exportCompaniesToExcel(list);
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Не вдалося вивантажити Excel',
          type: 'error',
        }),
      );
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, dispatch, searchQuery]);

  const handleSortingDirectionToggle = useCallback(() => {
    const next = !isRotated;
    setIsRotated(next);
    pageIndexRef.current = 0;
    orderByRef.current = 'name';
    orderDirectionRef.current = next ? 'DESC' : 'ASC';
    loadCompanies(dispatch, pageIndexRef, orderDirectionRef, orderByRef);
  }, [dispatch, isRotated]);

  const handleTermSort = useCallback(() => {
    const next = !isRotated;
    setIsRotated(next);
    pageIndexRef.current = 0;
    orderByRef.current = 'contractFinishAt';
    orderDirectionRef.current = next ? 'DESC' : 'ASC';
    loadCompanies(dispatch, pageIndexRef, orderDirectionRef, orderByRef);
  }, [dispatch, isRotated]);

  const companyListClass =
    companies.length <= 8 ||
    (isSearching && !loading && searchResults.length <= 8)
      ? style.wrapperCompanyExtra
      : style.wrapperCompany;

  const companiesToDisplay =
    searchResults.length > 0 && isSearching ? searchResults : companies;

  const observer = useRef<IntersectionObserver | null>(null);

  const lastCompanyRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading || !hasMore || isSearching) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            pageIndexRef.current += 1;
            loadCompanies(
              dispatch,
              pageIndexRef,
              orderDirectionRef,
              orderByRef,
            );
          }
        },
        {
          rootMargin: '100px',
          threshold: 0.1,
        },
      );
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, isSearching],
  );

  const handleClientMenuClick = (companyId: number, index: number) => {
    setSelectedClientId(companyId);
    setButtonRef({ current: companyRefs.current[index] });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedClientId(null);
  };

  // §FB37: усі розділи картки — вкладки сторінки компанії.
  const handleWidgetQr = (id: number) => {
    navigate(`/company/${id}/widget`);
    handleCloseModal();
  };

  const handleTabletQr = (id: number) => {
    navigate(`/company/${id}/nfc`);
    handleCloseModal();
  };

  const handleEmailHistory = (id: number) => {
    navigate(`/company/${id}/emails`);
    handleCloseModal();
  };

  const handleLocations = (id: number) => {
    navigate(`/company/${id}/locations`);
    handleCloseModal();
  };

  const handleDetails = (id: number) => {
    navigate(`/company/${id}`);
    handleCloseModal();
  };

  const handleDelete = () => {
    setShowConfirmModal(true);
    setIsModalOpen(false);
  };

  const handleCopyEmail = (e: React.MouseEvent, email: string, id: number) => {
    e.stopPropagation();
    if (!email) return;
    navigator.clipboard?.writeText(email);
    setCopiedId(id);
    setTimeout(() => setCopiedId((prev) => (prev === id ? null : prev)), 1500);
    dispatch(
      showSnackbar({ message: 'Email скопійовано в буфер', type: 'success' }),
    );
  };

  const handleSendInvitation = async (id: number) => {
    // §FB38: компанію шукаємо і серед пошукових результатів; успіх показуємо ЛИШЕ
    // після успішної відповіді API, помилку — тостом.
    const company = resolveCompany(id);
    if (!company) return;

    try {
      await resendCompanyPasswordAndQr(id);
      setSuccessEmail(company.email);
      setShowSuccessModal(true);
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Не вдалося надіслати запрошення — спробуйте ще раз',
          type: 'error',
        }),
      );
    } finally {
      handleCloseModal();
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
  };

  const handleSaveCompany = () => {
    setShowCompanyForm(false);
    loadCompanies(dispatch, pageIndexRef, orderDirectionRef, orderByRef);
  };

  const handleAddCompany = () => {
    setShowCompanyForm(true);
  };

  // Пошук компанії за id серед завантаженого списку та результатів пошуку.
  const resolveCompany = (id: number | null) =>
    companies.find((c) => c.id === id) ||
    searchResults.find((c) => c.id === id);

  const handleEmployeesModalOpen = (companyId: number | null) => {
    if (companyId !== null) navigate(`/company/${companyId}/employees`);
    setIsModalOpen(false);
  };

  const openEmployeesModal = (companyId: number) => {
    navigate(`/company/${companyId}/employees`);
  };

  return (
    <div className={style.company}>
      <ControlPanel
        setIsSearching={setIsSearching}
        onSearch={handleSearch}
        onAddCompany={handleAddCompany}
        onExport={handleExportExcel}
        isExporting={isExporting}
      />
      <div
        className={`${companyListClass} ${isSearching && companiesToDisplay.length <= 8 ? style.wrapperCompanyExtra : ''}`}
      >
        <CustomerListHead
          sortingLogo={orderDirection === 'asc'}
          isRotated={isRotated}
          toggleSortingDirection={handleSortingDirectionToggle}
          handleSortArrowClick={handleTermSort}
        />
        <div className={style.companyList}>
          {loading && companiesToDisplay.length === 0 ? (
            <SkeletonRows />
          ) : companiesToDisplay.length === 0 ? (
            <p className={style.noResults}>
              {isSearching
                ? 'Компанію не знайдено. Спробуйте іншу назву або додайте нову компанію, щоб розпочати роботу.'
                : 'Поки що немає жодної компанії.'}
            </p>
          ) : (
            companiesToDisplay.map((company: CompanyType, index: number) => {
              const finish = company.contractFinishAt
                ? new Date(company.contractFinishAt)
                : null;
              const daysLeft = finish
                ? Math.ceil((finish.getTime() - Date.now()) / 86400000)
                : null;
              const isCopied = copiedId === company.id;
              const display = company.name || company.userName || '';
              return (
                <div
                  key={`${company.id}-${index}`}
                  className={`${style.companyItem} ${
                    index % 2 === 0 ? style.rowEven : style.rowOdd
                  } ${
                    index === companiesToDisplay.length - 1
                      ? style.lastCompanyItem
                      : ''
                  }`}
                  ref={
                    index === companiesToDisplay.length - 1
                      ? lastCompanyRef
                      : null
                  }
                  onClick={() => handleDetails(company.id)}
                >
                  <p className={style.rowNum}>{index + 1}</p>

                  <div className={style.cellCompany}>
                    <div className={style.wrapAvatar}>
                      {company.avatarUrl ? (
                        <img
                          src={company.avatarUrl}
                          alt=""
                          className={style.avatar}
                        />
                      ) : (
                        <span
                          className={style.avatarInitial}
                          style={{ backgroundColor: avatarColor(display) }}
                        >
                          {initialOf(display)}
                        </span>
                      )}
                    </div>
                    <div className={style.cellText}>
                      <p className={style.primaryText}>{display}</p>
                      <p className={style.secondaryText}>
                        ЄДРПОУ {company.edrpou || '—'}
                      </p>
                    </div>
                  </div>

                  <div className={style.cellText}>
                    <p className={style.primaryText}>
                      {company.directorUserName || '—'}
                    </p>
                    <span
                      role="button"
                      tabIndex={0}
                      className={style.emailCopy}
                      title="Натисніть, щоб скопіювати"
                      onClick={(e) =>
                        handleCopyEmail(e, company.email, company.id)
                      }
                    >
                      {isCopied ? (
                        <span className={style.copiedText}>✓ Скопійовано</span>
                      ) : (
                        <span className={style.secondaryText}>
                          {company.email}
                        </span>
                      )}
                      {!isCopied && company.email && <CopyIcon />}
                    </span>
                  </div>

                  <div className={style.cellMid}>
                    <span
                      className={`${style.planPill} ${getPlanClass(
                        company.plan,
                      )}`}
                    >
                      {company.plan}
                    </span>
                  </div>

                  <div className={style.cellMid}>
                    <span
                      className={`${style.termBadge} ${
                        !finish
                          ? style.termInfinite
                          : daysLeft !== null && daysLeft < 30
                            ? style.termExpiring
                            : style.termNormal
                      }`}
                    >
                      <CalendarIcon />
                      {finish
                        ? finish.toLocaleDateString('uk-UA', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                          })
                        : 'Безстроковий'}
                    </span>
                  </div>

                  <a
                    className={style.phoneLink}
                    href={`tel:${company.phone}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <PhoneIcon />
                    <span className={style.tnum}>{company.phone}</span>
                  </a>

                  <button
                    ref={(el) => (companyRefs.current[index] = el)}
                    className={style.clientMenu}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClientMenuClick(company.id, index);
                    }}
                  >
                    <MoreVert />
                  </button>
                </div>
              );
            })
          )}
        </div>
        {isModalOpen && (
          <ModalWindowCompany
            company={[...companies, ...searchResults]}
            selectedClientId={selectedClientId}
            onClose={handleCloseModal}
            onDetails={handleDetails}
            onSendInvitation={handleSendInvitation}
            onDelete={handleDelete}
            buttonRef={buttonRef!}
            onShowEmployees={() => handleEmployeesModalOpen(selectedClientId)}
            onWidgetQr={handleWidgetQr}
            onTabletQr={handleTabletQr}
          />
        )}
        {showConfirmModal && selectedClientId !== null && (
          <ConfirmModal
            userName={resolveCompany(selectedClientId)?.name}
            avatarUrl={resolveCompany(selectedClientId)?.avatarUrl}
            onConfirm={async () => {
              try {
                await handleConfirmDelete(
                  selectedClientId,
                  () =>
                    loadCompanies(
                      dispatch,
                      pageIndexRef,
                      orderDirectionRef,
                      orderByRef,
                    ),
                  setShowConfirmModal,
                );
              } catch {
                dispatch(
                  showSnackbar({
                    message: 'Не вдалося видалити компанію — спробуйте ще раз',
                    type: 'error',
                  }),
                );
              }
            }}
            onCancel={handleCancelDelete}
            type={'Company'}
          />
        )}
        {showCompanyForm && (
          <CompanyForm
            onClose={() => setShowCompanyForm(false)}
            onSave={handleSaveCompany}
            isEdit={false}
          />
        )}
        {showSuccessModal && (
          <SuccessModal
            email={successEmail}
            onClose={() => setShowSuccessModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default Company;
